import type { useRelatedDocumentsPanelQueries } from './useRelatedDocumentsPanelQueries';
import type { RelatedDocumentsPanelProps } from '../RelatedDocumentsPanel.types';
import type { useRelatedDocumentsPanelMutations } from './useRelatedDocumentsPanelMutations';
import type { RelatedDocumentsPanelData } from './useRelatedDocumentsPanelData.types';
import type { AddRelatedDocumentsModalHandlers } from './useRelatedDocumentsPanelHandlers.types';

import lodash from 'lodash';
import queryString from 'utils/query-string';

import {
  RIGHT_PANEL_QUERY_PARAMS,
  OVERLAY_TYPES,
  OVERLAY_QUERY_PARAMS,
  RIGHT_PANEL_TYPES,
  DOCUMENT_DETAILS_TAB_KEY
} from 'shared/features/rightPanel/rightPanel.constants';
import { SUPPORTED_QUERY_PARAMS } from 'constants/PrimaryFilter';
import {
  RELATED_DOCUMENT_RELATION_TYPES,
  MAX_RELATED_DOCUMENT_ALERTS_COUNT
} from 'constants/RelatedDocuments';
import { SKU_RESTRICTION_TYPES } from 'constants/SKUs';
import { DOCUMENT_SUMMARY_PAGE_KEY } from 'constants/DocumentConstants';
import { DOCUMENT_CONTEXT_MENU_TEXTS } from 'constants/DocumentContextMenu';
import { DEFAULT_RELATION_TYPES_EXPANDED } from '../RelatedDocumentsPanel.constants';
import { NAV_BAR_ITEM_SECTIONS } from 'common/AppNavigation/hooks/useAppNavigationNavBarConfig.constants';

import * as routes from 'constants/Routes';
import * as urlUtils from 'utils/url-utils';
import * as rightPanelConstants from 'shared/features/rightPanel/rightPanel.constants';

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRightPanelReduxActions } from 'shared/features/rightPanel/hooks';
import { useSKUs } from 'utils/hooks';
import { useQueryParams } from '@compliance.ai/web-components';

export const useRelatedDocumentsPanelHandlers = ({
  props,
  localState,
  localActions,
  reduxState,
  queries,
  mutations
}: {
  props: Pick<RelatedDocumentsPanelProps, 'originalDocID'>;
  localState: RelatedDocumentsPanelData['localState'];
  localActions: RelatedDocumentsPanelData['localActions'];
  reduxState: RelatedDocumentsPanelData['reduxState'];
  queries: ReturnType<typeof useRelatedDocumentsPanelQueries>;
  mutations: ReturnType<typeof useRelatedDocumentsPanelMutations>;
}): AddRelatedDocumentsModalHandlers => {
  const { userHasSKU, showSKURestrictModal } = useSKUs();

  const rightPanelReduxActions = useRightPanelReduxActions();

  const navigate = useNavigate();
  const queryParams = useQueryParams();

  const handleDocumentOptionClick: AddRelatedDocumentsModalHandlers['handleDocumentOptionClick'] =
    relationType => options => {
      if (lodash.isEmpty(options) || lodash.isEmpty(options[0]) || !options[0].value) {
        return;
      }

      if (relationType === RELATED_DOCUMENT_RELATION_TYPES.TRANSLATION) {
        if (!userHasSKU(SKU_RESTRICTION_TYPES.AUTOTRANSLATE)) {
          return showSKURestrictModal(SKU_RESTRICTION_TYPES.AUTOTRANSLATE);
        }
      } else if (relationType === RELATED_DOCUMENT_RELATION_TYPES.ORGANIZATION_ALERT) {
        const alertUrl = options[0]?.url as string;
        return alertUrl ? window.open(alertUrl) : null;
      }

      const docId = options[0].value;

      const url = urlUtils.formatRedirectUrl({
        path: window.location.origin + '/' + NAV_BAR_ITEM_SECTIONS.DASHBOARD,
        params: {
          ...queryParams,
          [SUPPORTED_QUERY_PARAMS.DOC_ID]: docId,
          [rightPanelConstants.OVERLAY_QUERY_PARAMS.OVERLAY_TYPE]:
            rightPanelConstants.OVERLAY_TYPES.PDF,
          [rightPanelConstants.RIGHT_PANEL_QUERY_PARAMS.RIGHT_PANEL_TYPE]:
            rightPanelConstants.RIGHT_PANEL_TYPES.DOCUMENT_DETAILS,
          [rightPanelConstants.RIGHT_PANEL_QUERY_PARAMS.SUMMARY_PAGE]:
            rightPanelConstants.DOCUMENT_DETAILS_TAB_QUERY_PARAM_VALUES[
              DOCUMENT_DETAILS_TAB_KEY.DETAILS
            ],
          [rightPanelConstants.RIGHT_PANEL_QUERY_PARAMS.CLOSE_RIGHT_PANEL_ON_OVERLAY_CLOSE]: true,
          [rightPanelConstants.RIGHT_PANEL_QUERY_PARAMS.IS_CLOSABLE]: true,
          [rightPanelConstants.RIGHT_PANEL_QUERY_PARAMS.SUMMARY_ID]: docId
        }
      });

      window.open(url, '_blank');
    };

  const handleAddModalOpen: AddRelatedDocumentsModalHandlers['handleAddModalOpen'] = useCallback(
    relationType => () => {
      if (relationType === RELATED_DOCUMENT_RELATION_TYPES.ORGANIZATION) {
        localActions.setAddModal({ isOpen: true, relationType });
      } else if (relationType === RELATED_DOCUMENT_RELATION_TYPES.ORGANIZATION_ALERT) {
        const isOpen =
          MAX_RELATED_DOCUMENT_ALERTS_COUNT >
          queries.fetchRelatedAlerts.data?.[relationType]?.selectedIds?.length;
        localActions.setAddModal({ isOpen, relationType });
      }
    },
    [localActions, queries.fetchRelatedAlerts.data]
  );

  const handleAddModalClose: AddRelatedDocumentsModalHandlers['handleAddModalClose'] =
    useCallback(() => {
      localActions.setAddModal({ isOpen: false, relationType: undefined });
    }, [localActions]);

  const handleDeleteModalOpen: AddRelatedDocumentsModalHandlers['handleDeleteModalOpen'] =
    useCallback(
      relationType => () => {
        if (relationType === RELATED_DOCUMENT_RELATION_TYPES.ORGANIZATION) {
          localActions.setDeleteModal({ isOpen: true, relationType });
        }
      },
      [localActions]
    );

  const handleDeleteModalClose: AddRelatedDocumentsModalHandlers['handleDeleteModalClose'] =
    useCallback(() => {
      localActions.setDeleteModal({ isOpen: false, relationType: undefined });
    }, [localActions]);

  const handleDocumentOptionDelete: AddRelatedDocumentsModalHandlers['handleDocumentOptionDelete'] =
    useCallback(
      relationType => async option => {
        if (lodash.isEmpty(option) || !option.value) {
          return;
        }

        switch (relationType) {
          case RELATED_DOCUMENT_RELATION_TYPES.ORGANIZATION: {
            mutations.deleteRelatedDocument.mutate(option.value as number);
            break;
          }
          case RELATED_DOCUMENT_RELATION_TYPES.ORGANIZATION_ALERT: {
            mutations.deleteRelatedAlert.mutate(option.value as number);
            break;
          }
        }
      },
      [mutations.deleteRelatedAlert, mutations.deleteRelatedDocument]
    );

  const handleShowMoreClick: AddRelatedDocumentsModalHandlers['handleShowMoreClick'] = useCallback(
    (relationType, listConfig) => () => {
      if (relationType === RELATED_DOCUMENT_RELATION_TYPES.ORGANIZATION) {
        return handleDeleteModalOpen(relationType)();
      }
      if (relationType === RELATED_DOCUMENT_RELATION_TYPES.CITATION) {
        navigate(
          `${routes.content}?${queryString.stringify({
            advanced_search: `doc_id IN [${listConfig.allIds}]`,
            search_sort: 'publication_date',
            order: 'desc'
          })}`
        );
      } else {
        navigate(
          `${routes.content}?${queryString.stringify({
            related_doc_id: props.originalDocID,
            search_sort: 'publication_date',
            order: 'desc'
          })}`
        );
      }

      rightPanelReduxActions.openRightPanel({
        type: RIGHT_PANEL_TYPES.DOCUMENT_DETAILS,
        props: {
          documentId: props.originalDocID,
          activeTabKey: DOCUMENT_DETAILS_TAB_KEY.RELATED_DOCUMENTS
        }
      });
    },
    [navigate, props.originalDocID, rightPanelReduxActions, handleDeleteModalOpen]
  );

  const handleExpandButtonClick: AddRelatedDocumentsModalHandlers['handleExpandButtonClick'] =
    useCallback(
      relationType => isOpen => {
        localActions.setExpanded({
          ...localState.expanded,
          [relationType]: isOpen
        });
      },
      [localState, localActions]
    );

  const handleDefaultExpandedTypeChange = useCallback(() => {
    if (reduxState.expandedRelatedDocsType) {
      localActions.setExpanded({
        ...DEFAULT_RELATION_TYPES_EXPANDED,
        [reduxState.expandedRelatedDocsType]: true
      });
    }
  }, [localActions, reduxState.expandedRelatedDocsType]);

  const handleGetContextMenuOptions: AddRelatedDocumentsModalHandlers['handleGetContextMenuOptions'] =
    useCallback(option => {
      if (!option.value) {
        return;
      }

      const stringifiedQueryParams = queryString.stringify({
        [RIGHT_PANEL_QUERY_PARAMS.CLOSE_RIGHT_PANEL_ON_OVERLAY_CLOSE]: true,
        [RIGHT_PANEL_QUERY_PARAMS.SUMMARY_PAGE]: DOCUMENT_SUMMARY_PAGE_KEY,
        [RIGHT_PANEL_QUERY_PARAMS.SUMMARY_ID]: option.value,
        [OVERLAY_QUERY_PARAMS.OVERLAY_TYPE]: OVERLAY_TYPES.PDF
      });

      const url = `${routes.dashboard}?${stringifiedQueryParams}`;

      return Object.values(DOCUMENT_CONTEXT_MENU_TEXTS).map(contextMenuText => ({
        label: contextMenuText,
        onClick: () => {
          if (contextMenuText === DOCUMENT_CONTEXT_MENU_TEXTS.OPEN_DOCUMENT_NEW_WINDOW) {
            window.open(url, '_blank', 'toolbar=yes,scrollbars=yes,resizable=yes,fullscreen=yes');
          } else {
            window.open(url);
          }
        }
      }));
    }, []);

  return {
    handleDefaultExpandedTypeChange,
    handleDocumentOptionClick,
    handleAddModalOpen,
    handleAddModalClose,
    handleDeleteModalOpen,
    handleDeleteModalClose,
    handleDocumentOptionDelete,
    handleShowMoreClick,
    handleExpandButtonClick,
    handleGetContextMenuOptions
  };
};
